import { Koi } from '@ksyos/koi';
import type { TypedSchema } from '@common/validation/utils/typedSchemaType';
import {
    ComorbiditeitenBehandelingenOpties,
    ComorbiditeitenMedicatieOpties,
    DiabetesMellitusOpties,
    LeverziektenOpties,
    MaligniteitenOpties,
} from '@common/customControls/bariatrics';
import {
    hba1cSchema,
    hdlCholesterolSchema,
    ldlCholesterolSchema,
    ontlastingFrequentiePerWeekSchema,
    triglyceridenSchema,
} from '@common/validation/schemas';
import { addAllowNull } from '@common/validation/utils/addAllowNull';
import { getTextAreaConditionalSchema } from '@common/validation/utils/getTextAreaConditionalSchema';
import { getTextAreaStringSchema } from '@common/validation/utils/getTextAreaStringSchema';
import type {
    AanvullendeOnderzoeken,
    Anamnese,
    BariPreNurseSpecialistMedScreeningReportBaseData,
    Comorbiditeiten,
    Conclusie,
    FamiliaireBelasting,
    LichamelijkOnderzoek,
    Medicatie,
    SpecieleAnamnese,
} from './bariPreNurseSpecialistMedScreeningReportData';
import {
    ConsistentieOntlastingOpties,
    OntlastingspatroonOpties,
    OperatieTypeWensOpties,
    PatientIsMobielOpties,
    PatientIsVerwezenDoorOpties,
    VitaminedeficientiesOpties,
} from './bariPreNurseSpecialistMedScreeningReportData';

const typedGetTextAreaConditionalSchema =
    getTextAreaConditionalSchema<BariPreNurseSpecialistMedScreeningReportBaseData>;

export const AnamneseFinal: TypedSchema<Anamnese> = {
    consult_MDL_arts_gehad: Koi.boolean().allow(null),
    consult_MDL_arts_toelichting: typedGetTextAreaConditionalSchema('consult_MDL_arts_gehad', true),
    consult_internist_gehad: Koi.boolean().allow(null),
    consult_internist_toelichting: typedGetTextAreaConditionalSchema('consult_internist_gehad', true),
    eerdere_operaties: Koi.boolean(),
    eerdere_operaties_toelichting: typedGetTextAreaConditionalSchema('eerdere_operaties'),
    helicobacter_pylori_behandeld_met_antibiotica: Koi.when('helicobacter_pylori_sprake_van', {
        is: true,
        otherwise: Koi.valid(null),
        then: Koi.boolean(),
    }),
    helicobacter_pylori_behandeld_met_antibiotica_type: typedGetTextAreaConditionalSchema(
        'helicobacter_pylori_behandeld_met_antibiotica'
    ),
    helicobacter_pylori_onderzoek_datum: typedGetTextAreaConditionalSchema('helicobacter_pylori_onderzoek_gehad'),
    helicobacter_pylori_onderzoek_gehad: Koi.boolean(),
    helicobacter_pylori_sprake_van: Koi.when('helicobacter_pylori_onderzoek_gehad', {
        is: true,
        otherwise: Koi.valid(null),
        then: Koi.boolean(),
    }),
    medische_voorgeschiedenis: getTextAreaStringSchema(),
    vitaminedeficienties: Koi.koi().enum(VitaminedeficientiesOpties),
    vitaminedeficienties_toelichting: Koi.when('vitaminedeficienties', {
        is: VitaminedeficientiesOpties.ja,
        otherwise: getTextAreaStringSchema(true),
        then: getTextAreaStringSchema(),
    }),
};

export const SpecieleAnamneseFinal: TypedSchema<SpecieleAnamnese> = {
    buikklachten: Koi.boolean(),
    buikklachten_toelichting: typedGetTextAreaConditionalSchema('buikklachten'),
    consistentie_ontlasting: Koi.koi().enum(ConsistentieOntlastingOpties),
    consistentie_ontlasting_overig: Koi.when('consistentie_ontlasting', {
        is: ConsistentieOntlastingOpties.overig,
        otherwise: Koi.valid(null),
        then: getTextAreaStringSchema(),
    }),
    duur_overgewicht_in_jaren: Koi.number().min(0).max(99).precision(1),
    motivatie_behandeling: getTextAreaStringSchema(),
    ontlasting_frequentie_per_week: Koi.when('ontlastingspatroon', {
        is: OntlastingspatroonOpties.anders,
        otherwise: Koi.valid(null),
        then: ontlastingFrequentiePerWeekSchema,
    }),
    ontlastingspatroon: Koi.koi().enum(OntlastingspatroonOpties),
    patient_is_verwezen_door: Koi.koi().enum(PatientIsVerwezenDoorOpties),
    toelichting_speciele_anamnese: getTextAreaStringSchema(true),
};

export const MedicatieFinal: TypedSchema<Medicatie> = {
    allergieen: getTextAreaStringSchema(),
    medicatie: Koi.boolean(),
    medicatie_toelichting: typedGetTextAreaConditionalSchema('medicatie'),
};

export const FamiliaireBelastingFinal: TypedSchema<FamiliaireBelasting> = {
    diabetes_mellitus: Koi.boolean(),
    hart_en_vaatziekten: Koi.boolean(),
    hartfalen: Koi.boolean(),
    hypertensie: Koi.boolean(),
    overgewicht: Koi.boolean(),
    overgewicht_bij_wie_options: Koi.when('overgewicht', {
        is: true,
        otherwise: Koi.object({
            overgewicht_broer_zus: Koi.valid(null),
            overgewicht_moeder: Koi.valid(null),
            overgewicht_vader: Koi.valid(null),
        }),
        then: Koi.object({
            overgewicht_broer_zus: Koi.boolean().allow(null),
            overgewicht_moeder: Koi.boolean().allow(null),
            overgewicht_vader: Koi.boolean().allow(null),
        }).anyFilledIn(),
    }),
};

export const LichamelijkOnderzoekFinal: TypedSchema<LichamelijkOnderzoek> = {
    abdominaal: getTextAreaStringSchema(true),
    extremiteiten: getTextAreaStringSchema(true),
    littekens_abdomen: Koi.boolean(),
    littekens_toelichting: typedGetTextAreaConditionalSchema('littekens_abdomen'),
    patient_is_mobiel: Koi.koi().enum(PatientIsMobielOpties).allow(null),
    smetten: Koi.boolean().allow(null),
    viscerale_obesitas: Koi.boolean().allow(null),
};

export const ComorbiditeitenFinal: TypedSchema<Comorbiditeiten> = {
    GERD: Koi.koi().enum(ComorbiditeitenMedicatieOpties),
    HDL_cholesterol: hdlCholesterolSchema.allow(null),
    HIV_AIDS: Koi.boolean(),
    LDL_cholesterol: ldlCholesterolSchema.allow(null),
    OSAS: Koi.koi().enum(ComorbiditeitenBehandelingenOpties),
    bindweefselziekte: Koi.boolean(),
    cerebrovasculaire_aandoening: Koi.boolean(),
    chronische_longziekte: Koi.boolean(),
    congestief_hartfalen: Koi.boolean(),
    dementie: Koi.boolean(),
    diabetes_mellitus_details: Koi.when('diabetes_mellitus_diagnose', {
        is: ComorbiditeitenMedicatieOpties.aanwezig,
        otherwise: Koi.when('diabetes_mellitus_diagnose', {
            is: ComorbiditeitenMedicatieOpties.aanwezig_met_medicatie,
            otherwise: Koi.valid(null),
            then: Koi.koi().enum(DiabetesMellitusOpties),
        }),
        then: Koi.koi().enum(DiabetesMellitusOpties),
    }),
    diabetes_mellitus_diagnose: Koi.koi().enum(ComorbiditeitenMedicatieOpties),
    dyslipidemie: Koi.koi().enum(ComorbiditeitenMedicatieOpties),
    gastrointestinaal_ulcuslijden: Koi.boolean(),
    gewrichtsklachten: Koi.koi().enum(ComorbiditeitenBehandelingenOpties),
    hba1c: hba1cSchema.allow(null),
    hypertensie_diagnose: Koi.koi().enum(ComorbiditeitenMedicatieOpties),
    klachten_van_reflux: Koi.boolean(),
    leverziekte: Koi.koi().enum(LeverziektenOpties),
    maligniteit: Koi.koi().enum(MaligniteitenOpties),
    myocardinfarct: Koi.boolean(),
    nierziekte: Koi.boolean(),
    para_hemiplegie: Koi.boolean(),
    perifeer_vaatlijden_aneurysma_aorta: Koi.boolean(),
    triglyceriden: triglyceridenSchema.allow(null),
    trombose: Koi.boolean(),
    verhoogd_risico_DVT_of_PE: Koi.boolean(),
};

export const AanvullendeOnderzoekenFinal: TypedSchema<AanvullendeOnderzoeken> = {
    ander_onderzoek: Koi.boolean().allow(null),
    ander_onderzoek_toelichting: typedGetTextAreaConditionalSchema('ander_onderzoek'),
    cardiologisch_onderzoek: Koi.boolean().allow(null),
    cardiologisch_onderzoek_toelichting: typedGetTextAreaConditionalSchema('cardiologisch_onderzoek'),
    longfunctieonderzoek: Koi.boolean().allow(null),
    longfunctieonderzoek_toelichting: getTextAreaConditionalSchema('longfunctieonderzoek'),
    onderzoek_interne_geneeskunde: Koi.boolean().allow(null),
    onderzoek_interne_geneeskunde_toelichting: getTextAreaConditionalSchema('onderzoek_interne_geneeskunde'),
    slaaponderzoek: Koi.boolean().allow(null),
    slaaponderzoek_toelichting: getTextAreaConditionalSchema('slaaponderzoek'),
};

export const ConclusieFinal: TypedSchema<Conclusie> = {
    contra_indicatie: Koi.boolean(),
    contra_indicatie_toelichting: getTextAreaConditionalSchema('contra_indicatie'),
    dagbehandeling: Koi.boolean(),
    dagbehandeling_toelichting: Koi.when('dagbehandeling', {
        is: false,
        otherwise: getTextAreaStringSchema(true),
        then: getTextAreaStringSchema(),
    }),
    wens_voor_type_operatie: Koi.koi().enum(OperatieTypeWensOpties),
    wens_voor_type_operatie_anders_toelichting: Koi.when('wens_voor_type_operatie', {
        is: OperatieTypeWensOpties.anders,
        otherwise: getTextAreaStringSchema(true),
        then: getTextAreaStringSchema(),
    }),
};

export const bariPreNurseSpecialistMedScreeningReportBaseFinal: TypedSchema<BariPreNurseSpecialistMedScreeningReportBaseData> =
    {
        ...AnamneseFinal,
        ...SpecieleAnamneseFinal,
        ...MedicatieFinal,
        ...FamiliaireBelastingFinal,
        ...LichamelijkOnderzoekFinal,
        ...AanvullendeOnderzoekenFinal,
        ...ConclusieFinal,
    };

export const bariPreNurseSpecialistMedScreeningReportBaseDraft: TypedSchema<BariPreNurseSpecialistMedScreeningReportBaseData> =
    addAllowNull({
        ...bariPreNurseSpecialistMedScreeningReportBaseFinal,
        overgewicht_bij_wie_options: Koi.object({
            overgewicht_broer_zus: Koi.boolean().allow(null),
            overgewicht_moeder: Koi.boolean().allow(null),
            overgewicht_vader: Koi.boolean().allow(null),
        }),
    });
